/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import axios from "axios";
import swal from "sweetalert";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Collapse from "@mui/material/Collapse";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDProgress from "components/MDProgress";
import MDButton from "components/MDButton";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Icons
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { logoutUser } from "actions/AuthAction";
import { useNavigate } from "react-router-dom";
import { API_ESA_OFFICE } from "utils";

function DaftarResponden() {
  const [layananList, setLayananList] = useState([]);
  const [selectedLayanan, setSelectedLayanan] = useState("");
  const [selectedTahun, setSelectedTahun] = useState("");
  const [tahunList, setTahunList] = useState([]);
  const [surveyList, setSurveyList] = useState([]);
  const [selectedSurvey, setSelectedSurvey] = useState("");
  const [respondenList, setRespondenList] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [dinasId, setDinasId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingResponden, setLoadingResponden] = useState(false);
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [selectedRespondenDetail, setSelectedRespondenDetail] = useState(null);
  const navigate = useNavigate();

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setDinasId(user.dinas_id);
    } else {
      logoutUser(navigate);
    }
  }, []);

  // Fetch layanan list
  const fetchLayanan = async () => {
    if (!dinasId) return;

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}survey-kepuasan/layanan-publik?dinasId=${dinasId}`
      );
      const layananData = response.data || [];
      setLayananList(layananData);

      // Auto select layanan pertama jika ada
      if (layananData.length > 0 && !selectedLayanan) {
        const firstLayananId = layananData[0].id.toString();
        setSelectedLayanan(firstLayananId);
        fetchSurvey(firstLayananId);
      }
    } catch (error) {
      console.error("Error fetching layanan:", error);
      swal({
        title: "Error",
        text: "Gagal mengambil data layanan publik",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dinasId) {
      fetchLayanan();
    }
  }, [dinasId]);

  // Fetch survey - selalu menggunakan survey dengan id = 2
  const fetchSurvey = async (layananId) => {
    if (!layananId) {
      setSurveyList([]);
      setSelectedSurvey("");
      return;
    }

    setLoading(true);
    try {
      // Selalu menggunakan survey dengan id = 2 karena survey kepuasan masyarakat selalu terkait dengan semua layanan
      const surveyId = "2";
      setSelectedSurvey(surveyId);

      // Fetch detail survey untuk mendapatkan nama survey
      try {
        const response = await axios.get(
          `${API_ESA_OFFICE}survey-kepuasan/survey/${surveyId}/detail`
        );
        const surveyDetail = response.data.survey;
        setSurveyList([
          {
            id: 2,
            nama_survey:
              surveyDetail.nama_survey ||
              surveyDetail.judul_survey ||
              "Survey Kepuasan Masyarakat",
          },
        ]);
      } catch (error) {
        console.error("Error fetching survey detail:", error);
        // Set default jika error
        setSurveyList([{ id: 2, nama_survey: "Survey Kepuasan Masyarakat" }]);
      }

      generateTahunList();
    } catch (error) {
      console.error("Error fetching survey:", error);
      swal({
        title: "Error",
        text: "Gagal mengambil data survey",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Generate tahun list dari current year sampai 2025
  const generateTahunList = () => {
    const currentYear = new Date().getFullYear();
    const tahunArray = [];

    // Generate dari current year sampai 2025
    for (let tahun = currentYear; tahun >= 2025; tahun--) {
      tahunArray.push(tahun);
    }

    setTahunList(tahunArray);

    // Set default tahun ke current year
    if (tahunArray.length > 0) {
      setSelectedTahun(currentYear.toString());
    }
  };

  // Fetch responden berdasarkan survey, layanan, dan tahun
  const fetchResponden = async (surveyId, tahun) => {
    if (!surveyId || !tahun || !selectedLayanan) {
      setRespondenList([]);
      return;
    }

    setLoadingResponden(true);
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}survey-kepuasan/survey/${surveyId}/responses?limit=1000&offset=0`
      );

      // Filter berdasarkan tahun dan id_layanan
      const layananId = parseInt(selectedLayanan);
      const filteredData = (response.data?.data || []).filter((responden) => {
        // Filter berdasarkan tahun
        if (!responden.datetime_submit) return false;
        const respondenTahun = new Date(
          responden.datetime_submit
        ).getFullYear();
        if (respondenTahun.toString() !== tahun.toString()) return false;

        // Filter berdasarkan id_layanan
        const respondenLayananId = responden.id_layanan;
        return respondenLayananId === layananId;
      });

      setRespondenList(filteredData);
    } catch (error) {
      console.error("Error fetching responden:", error);
      swal({
        title: "Error",
        text: "Gagal mengambil data responden",
        icon: "error",
      });
    } finally {
      setLoadingResponden(false);
    }
  };

  // Handle layanan change
  const handleLayananChange = (e) => {
    const layananId = e.target.value;
    setSelectedLayanan(layananId);
    setSelectedTahun("");
    setRespondenList([]);
    setTahunList([]);
    // Survey selalu id = 2, tidak perlu di-reset
    if (layananId) {
      fetchSurvey(layananId);
    }
  };

  // Handle survey change - disabled karena selalu menggunakan survey id = 2
  const handleSurveyChange = (e) => {
    // Survey selalu id = 2, tidak perlu diubah
    // Tetap set untuk konsistensi
    setSelectedSurvey("2");
    setSelectedTahun("");
    setRespondenList([]);
    generateTahunList();
  };

  // Handle tahun change
  const handleTahunChange = (e) => {
    const tahun = e.target.value;
    setSelectedTahun(tahun);
    setRespondenList([]);
    if (tahun && selectedSurvey) {
      fetchResponden(selectedSurvey, tahun);
    }
  };

  // Auto fetch responden ketika survey, layanan, dan tahun sudah dipilih
  useEffect(() => {
    if (selectedSurvey && selectedTahun && selectedLayanan) {
      fetchResponden(selectedSurvey, selectedTahun);
    }
  }, [selectedSurvey, selectedTahun, selectedLayanan]);

  // Handle open detail modal
  const handleOpenDetailModal = (responden) => {
    // Pastikan kita menggunakan data dari respondenList yang memiliki jawaban lengkap
    const fullRespondenData =
      respondenList.find((r) => r.id === responden.id) || responden;
    setSelectedRespondenDetail(fullRespondenData);
    setOpenDetailModal(true);
  };

  // Handle close detail modal
  const handleCloseDetailModal = () => {
    setOpenDetailModal(false);
    setSelectedRespondenDetail(null);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Group jawaban by kategori
  const groupJawabanByKategori = (jawaban) => {
    const grouped = {};
    jawaban.forEach((j) => {
      const kategori = j.kategori || "Lainnya";
      if (!grouped[kategori]) {
        grouped[kategori] = [];
      }
      grouped[kategori].push(j);
    });
    return grouped;
  };

  // Get nilai display
  const getNilaiDisplay = (jawaban) => {
    if (jawaban.tipe === "nilai") {
      return jawaban.opsi_label || `Nilai: ${jawaban.nilai || "-"}`;
    } else if (jawaban.tipe === "teks") {
      return jawaban.jawaban_teks || "-";
    }
    return "-";
  };

  // Calculate rekap dari responden list
  const calculateRekap = () => {
    if (!respondenList || respondenList.length === 0) {
      return {
        totalResponden: 0,
        rataNilai: 0,
        skorSKM: 0,
        perPertanyaan: [],
      };
    }

    // Kumpulkan semua jawaban dengan tipe "nilai"
    const jawabanNilai = [];
    respondenList.forEach((responden) => {
      if (responden.jawaban && Array.isArray(responden.jawaban)) {
        responden.jawaban.forEach((jawaban) => {
          if (jawaban.tipe === "nilai" && jawaban.nilai !== null) {
            jawabanNilai.push({
              nilai: parseFloat(jawaban.nilai) || 0,
              pertanyaan: jawaban.pertanyaan || "Lainnya",
              id_pertanyaan: jawaban.id_pertanyaan,
            });
          }
        });
      }
    });

    // Hitung rata-rata keseluruhan
    const totalNilai = jawabanNilai.reduce((sum, j) => sum + j.nilai, 0);
    const rataNilai =
      jawabanNilai.length > 0 ? totalNilai / jawabanNilai.length : 0;
    const skorSKM = rataNilai * 25;

    // Hitung per pertanyaan
    const pertanyaanMap = {};
    jawabanNilai.forEach((j) => {
      const key = j.id_pertanyaan || j.pertanyaan;
      if (!pertanyaanMap[key]) {
        pertanyaanMap[key] = {
          pertanyaan: j.pertanyaan,
          nilai: [],
        };
      }
      pertanyaanMap[key].nilai.push(j.nilai);
    });

    const perPertanyaan = Object.keys(pertanyaanMap).map((key) => {
      const nilaiArray = pertanyaanMap[key].nilai;
      const totalNilaiPertanyaan = nilaiArray.reduce((sum, n) => sum + n, 0);
      const rataNilaiPertanyaan =
        nilaiArray.length > 0 ? totalNilaiPertanyaan / nilaiArray.length : 0;
      return {
        pertanyaan: pertanyaanMap[key].pertanyaan,
        jumlahJawaban: nilaiArray.length,
        rataNilai: parseFloat(rataNilaiPertanyaan.toFixed(2)),
        skor: parseFloat((rataNilaiPertanyaan * 25).toFixed(2)),
      };
    });

    return {
      totalResponden: respondenList.length,
      rataNilai: parseFloat(rataNilai.toFixed(2)),
      skorSKM: parseFloat(skorSKM.toFixed(2)),
      perPertanyaan: perPertanyaan.sort((a, b) =>
        a.pertanyaan.localeCompare(b.pertanyaan)
      ),
    };
  };

  const rekapData = calculateRekap();

  // Calculate rata-rata nilai per responden
  const calculateRataRataResponden = (responden) => {
    if (!responden.jawaban || responden.jawaban.length === 0) return 0;
    const jawabanNilai = responden.jawaban.filter(
      (j) => j.tipe === "nilai" && j.nilai !== null
    );
    if (jawabanNilai.length === 0) return 0;
    const totalNilai = jawabanNilai.reduce(
      (sum, j) => sum + (parseFloat(j.nilai) || 0),
      0
    );
    return parseFloat((totalNilai / jawabanNilai.length).toFixed(2));
  };

  // Calculate skor per responden (rata-rata * 25)
  const calculateSkorResponden = (responden) => {
    const rataRata = calculateRataRataResponden(responden);
    return parseFloat((rataRata * 25).toFixed(2));
  };

  // Define columns for DataTable
  const columns = [
    { Header: "No", accessor: "no", width: "5%" },
    { Header: "Nama Responden", accessor: "nama", width: "15%" },
    { Header: "Pendidikan", accessor: "pendidikan", width: "10%" },
    { Header: "Pekerjaan", accessor: "pekerjaan", width: "10%" },
    { Header: "Tanggal Submit", accessor: "datetime_submit", width: "15%" },
    {
      Header: "Rata-rata Nilai",
      accessor: "rata_rata",
      width: "12%",
      Cell: ({ value }) => (
        <MDTypography variant="body2" fontWeight="medium">
          {value > 0 ? value.toFixed(2) : "0.00"}
        </MDTypography>
      ),
    },
    {
      Header: "Skor",
      accessor: "skor",
      width: "12%",
      Cell: ({ value }) => (
        <MDTypography variant="body2" fontWeight="medium" color="info">
          {value > 0 ? value.toFixed(2) : "0.00"}
        </MDTypography>
      ),
    },
    {
      Header: "Detail",
      accessor: "detail",
      width: "21%",
      Cell: ({ row }) => (
        <MDButton
          variant="outlined"
          color="info"
          size="small"
          onClick={() => handleOpenDetailModal(row.original)}
          startIcon={<VisibilityIcon />}
        >
          Lihat Detail
        </MDButton>
      ),
    },
  ];

  // Define rows for DataTable
  const rows = respondenList.map((responden, index) => ({
    id: responden.id,
    no: index + 1,
    nama: responden.nama || "-",
    pendidikan: responden.pendidikan || "-",
    pekerjaan: responden.pekerjaan || "-",
    datetime_submit: formatDate(responden.datetime_submit),
    rata_rata: calculateRataRataResponden(responden),
    skor: calculateSkorResponden(responden),
    detail: responden, // Store full responden object for modal
    original: responden, // Store original data with jawaban
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Daftar Responden Survey Kepuasan
                </MDTypography>
              </MDBox>
              <MDBox pt={4} pb={3} px={3}>
                {/* Filter Section */}
                <Grid container spacing={3} mb={3}>
                  <Grid item xs={12} md={4}>
                    <MDBox
                      mb={1}
                      ml={0.5}
                      lineHeight={0}
                      display="inline-block"
                    >
                      <MDTypography
                        component="label"
                        variant="button"
                        fontWeight="regular"
                        color="text"
                      >
                        Pilih Layanan
                      </MDTypography>
                    </MDBox>
                    <Autocomplete
                      value={
                        layananList.find(
                          (l) => l.id.toString() === selectedLayanan
                        ) || null
                      }
                      onChange={(event, newValue) => {
                        handleLayananChange({
                          target: {
                            value: newValue ? newValue.id.toString() : "",
                          },
                        });
                      }}
                      options={layananList}
                      getOptionLabel={(option) => option.layanan || ""}
                      disabled={loading}
                      renderInput={(params) => (
                        <MDInput {...params} variant="standard" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDBox
                      mb={1}
                      ml={0.5}
                      lineHeight={0}
                      display="inline-block"
                    >
                      <MDTypography
                        component="label"
                        variant="button"
                        fontWeight="regular"
                        color="text"
                      >
                        Pilih Survey
                      </MDTypography>
                    </MDBox>
                    <Autocomplete
                      value={
                        surveyList.find(
                          (s) => s.id.toString() === selectedSurvey
                        ) || null
                      }
                      onChange={(event, newValue) => {
                        handleSurveyChange({
                          target: {
                            value: newValue ? newValue.id.toString() : "",
                          },
                        });
                      }}
                      options={surveyList}
                      getOptionLabel={(option) => option.nama_survey || ""}
                      disabled={true} // Disabled karena selalu menggunakan survey id = 2
                      renderInput={(params) => (
                        <MDInput {...params} variant="standard" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDBox
                      mb={1}
                      ml={0.5}
                      lineHeight={0}
                      display="inline-block"
                    >
                      <MDTypography
                        component="label"
                        variant="button"
                        fontWeight="regular"
                        color="text"
                      >
                        Pilih Tahun
                      </MDTypography>
                    </MDBox>
                    <Autocomplete
                      value={
                        tahunList.find((t) => t.toString() === selectedTahun) ||
                        null
                      }
                      onChange={(event, newValue) => {
                        handleTahunChange({
                          target: {
                            value: newValue ? newValue.toString() : "",
                          },
                        });
                      }}
                      options={tahunList}
                      getOptionLabel={(option) => option.toString() || ""}
                      disabled={!selectedSurvey || loading}
                      renderInput={(params) => (
                        <MDInput {...params} variant="standard" />
                      )}
                    />
                  </Grid>
                </Grid>

                {/* Loading Indicator */}
                {loadingResponden && (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    py={4}
                  >
                    <CircularProgress />
                    <MDTypography variant="body2" color="text" ml={2}>
                      Memuat data responden...
                    </MDTypography>
                  </MDBox>
                )}

                {/* Rekap Card */}
                {!loadingResponden &&
                  selectedLayanan &&
                  selectedSurvey &&
                  selectedTahun && (
                    <Card sx={{ mb: 3 }}>
                      <MDBox p={3}>
                        <MDTypography variant="h6" mb={3}>
                          Rekapan Nilai Kepuasan
                        </MDTypography>
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={3}>
                            <MDBox>
                              <MDTypography
                                variant="caption"
                                color="text"
                                fontWeight="medium"
                              >
                                Total Responden
                              </MDTypography>
                              <MDTypography variant="h4" fontWeight="bold">
                                {rekapData.totalResponden}
                              </MDTypography>
                            </MDBox>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <MDBox>
                              <MDTypography
                                variant="caption"
                                color="text"
                                fontWeight="medium"
                              >
                                Skor SKM (25-100)
                              </MDTypography>
                              <MDTypography
                                variant="h4"
                                fontWeight="bold"
                                color="info"
                              >
                                {rekapData.skorSKM > 0
                                  ? rekapData.skorSKM.toFixed(2)
                                  : "0.00"}
                              </MDTypography>
                              <MDProgress
                                value={
                                  rekapData.skorSKM > 0 ? rekapData.skorSKM : 0
                                }
                                color="info"
                                variant="gradient"
                                label={false}
                              />
                            </MDBox>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <MDBox>
                              <MDTypography
                                variant="caption"
                                color="text"
                                fontWeight="medium"
                              >
                                Rata-rata Nilai (1-4)
                              </MDTypography>
                              <MDTypography
                                variant="h4"
                                fontWeight="bold"
                                color="success"
                              >
                                {rekapData.rataNilai > 0
                                  ? rekapData.rataNilai.toFixed(2)
                                  : "0.00"}
                              </MDTypography>
                              <MDProgress
                                value={
                                  rekapData.rataNilai > 0
                                    ? (rekapData.rataNilai / 4) * 100
                                    : 0
                                }
                                color="success"
                                variant="gradient"
                                label={false}
                              />
                            </MDBox>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <MDBox>
                              <MDTypography
                                variant="caption"
                                color="text"
                                fontWeight="medium"
                              >
                                Tahun
                              </MDTypography>
                              <MDTypography variant="h4" fontWeight="bold">
                                {selectedTahun}
                              </MDTypography>
                            </MDBox>
                          </Grid>
                        </Grid>

                        {/* Per Pertanyaan */}
                        {rekapData.perPertanyaan.length > 0 && (
                          <MDBox mt={4}>
                            <MDTypography variant="h6" mb={2}>
                              Rata-rata per Pertanyaan
                            </MDTypography>
                            <Grid container spacing={2}>
                              {rekapData.perPertanyaan.map(
                                (pertanyaan, index) => (
                                  <Grid item xs={12} sm={6} md={4} key={index}>
                                    <Card variant="outlined">
                                      <MDBox p={2}>
                                        <MDTypography
                                          variant="subtitle2"
                                          fontWeight="bold"
                                          mb={1}
                                        >
                                          {pertanyaan.pertanyaan}
                                        </MDTypography>
                                        <MDTypography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          Jumlah Jawaban:{" "}
                                          {pertanyaan.jumlahJawaban}
                                        </MDTypography>
                                        <MDBox mt={1}>
                                          <MDTypography
                                            variant="h6"
                                            color="info"
                                          >
                                            Skor: {pertanyaan.skor}
                                          </MDTypography>
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            Rata-rata: {pertanyaan.rataNilai}{" "}
                                            (1-4)
                                          </MDTypography>
                                          <MDProgress
                                            value={pertanyaan.skor}
                                            color="info"
                                            variant="gradient"
                                            label={false}
                                          />
                                        </MDBox>
                                      </MDBox>
                                    </Card>
                                  </Grid>
                                )
                              )}
                            </Grid>
                          </MDBox>
                        )}
                      </MDBox>
                    </Card>
                  )}

                {/* Data Table */}
                {!loadingResponden &&
                  selectedLayanan &&
                  selectedSurvey &&
                  selectedTahun && (
                    <>
                      {respondenList.length === 0 ? (
                        <MDBox
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          justifyContent="center"
                          py={4}
                        >
                          <MDTypography variant="h6" color="text.secondary">
                            Tidak ada responden untuk tahun {selectedTahun}
                          </MDTypography>
                        </MDBox>
                      ) : (
                        <>
                          <MDBox mb={2}>
                            <MDTypography variant="h6">
                              Total Responden: {respondenList.length}
                            </MDTypography>
                          </MDBox>
                          {isMobile ? (
                            <MDBox
                              display="flex"
                              flexDirection="column"
                              gap={2}
                            >
                              {respondenList.map((responden, index) => (
                                <Card key={responden.id}>
                                  <MDBox p={2}>
                                    <MDBox
                                      display="flex"
                                      justifyContent="space-between"
                                      alignItems="center"
                                      mb={2}
                                    >
                                      <MDTypography variant="h6">
                                        {index + 1}. {responden.nama || "-"}
                                      </MDTypography>
                                    </MDBox>
                                    <MDTypography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      Pendidikan: {responden.pendidikan || "-"}
                                    </MDTypography>
                                    <MDTypography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      Pekerjaan: {responden.pekerjaan || "-"}
                                    </MDTypography>
                                    <MDTypography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      Tanggal:{" "}
                                      {formatDate(responden.datetime_submit)}
                                    </MDTypography>
                                    <MDBox mt={2}>
                                      <MDButton
                                        variant="outlined"
                                        color="info"
                                        size="small"
                                        fullWidth
                                        onClick={() =>
                                          handleOpenDetailModal(responden)
                                        }
                                        startIcon={<VisibilityIcon />}
                                      >
                                        Lihat Detail
                                      </MDButton>
                                    </MDBox>
                                  </MDBox>
                                </Card>
                              ))}
                            </MDBox>
                          ) : (
                            <DataTable
                              table={{ columns, rows }}
                              entriesPerPage={{
                                defaultValue: 10,
                                entries: [5, 10, 15, 20, 25],
                              }}
                              showTotalEntries={true}
                              canSearch={true}
                            />
                          )}
                        </>
                      )}
                    </>
                  )}

                {/* Empty State - No Selection */}
                {!selectedLayanan && !loadingResponden && (
                  <MDBox
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    py={4}
                  >
                    <MDTypography variant="h6" color="text.secondary">
                      Pilih layanan, survey, dan tahun untuk melihat data
                      responden
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Modal Detail Jawaban Responden */}
      <Dialog
        open={openDetailModal}
        onClose={handleCloseDetailModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">Detail Jawaban Responden</MDTypography>
            <IconButton
              aria-label="close"
              onClick={handleCloseDetailModal}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          {selectedRespondenDetail && (
            <MDBox>
              {/* Informasi Responden */}
              <MDBox mb={3}>
                <MDTypography variant="h6" mb={2}>
                  Informasi Responden
                </MDTypography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <MDTypography variant="caption" color="text.secondary">
                      Nama
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="medium">
                      {selectedRespondenDetail.nama || "-"}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MDTypography variant="caption" color="text.secondary">
                      Pendidikan
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="medium">
                      {selectedRespondenDetail.pendidikan || "-"}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MDTypography variant="caption" color="text.secondary">
                      Pekerjaan
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="medium">
                      {selectedRespondenDetail.pekerjaan || "-"}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MDTypography variant="caption" color="text.secondary">
                      Tanggal Submit
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="medium">
                      {formatDate(selectedRespondenDetail.datetime_submit)}
                    </MDTypography>
                  </Grid>
                </Grid>
              </MDBox>

              {/* Jawaban Survey */}
              <MDBox>
                <MDTypography variant="h6" mb={2}>
                  Jawaban Survey
                </MDTypography>
                {selectedRespondenDetail.jawaban &&
                Array.isArray(selectedRespondenDetail.jawaban) &&
                selectedRespondenDetail.jawaban.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <MDTypography variant="caption" fontWeight="bold">
                              Pertanyaan
                            </MDTypography>
                          </TableCell>
                          <TableCell>
                            <MDTypography variant="caption" fontWeight="bold">
                              Jawaban
                            </MDTypography>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedRespondenDetail.jawaban.map((jawaban, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <MDTypography variant="body2">
                                {jawaban.pertanyaan || "-"}
                              </MDTypography>
                            </TableCell>
                            <TableCell>
                              <MDBox display="flex" alignItems="center" gap={1}>
                                <Chip
                                  label={getNilaiDisplay(jawaban)}
                                  color="info"
                                  size="small"
                                  variant="outlined"
                                />
                                {jawaban.tipe === "nilai" &&
                                  jawaban.nilai !== null && (
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      fontWeight="medium"
                                    >
                                      (Nilai:{" "}
                                      {parseFloat(jawaban.nilai).toFixed(2)})
                                    </MDTypography>
                                  )}
                              </MDBox>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    py={3}
                  >
                    <MDTypography variant="body2" color="text.secondary">
                      Tidak ada jawaban
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="gradient"
            color="info"
            onClick={handleCloseDetailModal}
          >
            Tutup
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default DaftarResponden;
